import { Card } from "../models/Card";
import { Deck } from "../models/Deck";
import { DeckConfig } from "../models/DeckConfig";
import { ValidationMessage } from "../models/ValidationMessage";
import { ProductRepository } from "./ProductRepository";

export interface DeckRule {
    /**
     * デッキの検証を行います。
     * @param deck 
     */
    validate(deck: Deck): ValidationMessage[] | null;

    /**
     * カードを追加しようとしているときに、事前検証を行います。
     * @param deck 
     * @param card 
     */
    preValidate(deck: Deck, card: Card, isFavorite: boolean): ValidationMessage[] | null;
}

export class EqualTotalDeckNumberOfSheets implements DeckRule {

    validate(deck: Deck): ValidationMessage[] | null {
        const errors: ValidationMessage[] = [];
        const totalCount = deck.favoriteCards.length + deck.normalCards.length;

        if (totalCount < DeckConfig.DeckTotalCount) {
            errors.push({
                key: "EqualTotalDeckNumberOfSheets-Less",
                level: "muted-warning",
                message: `カード枚数があと${DeckConfig.DeckTotalCount - totalCount}枚必要です。`
            });
        } else if (totalCount > DeckConfig.DeckTotalCount) {
            errors.push({
                key: "EqualTotalDeckNumberOfSheets-Greater",
                level: "warning",
                message: `カード枚数が${DeckConfig.DeckTotalCount}枚を超えています。`
            });
        }

        if (errors.length === 0) {
            return null;
        } else {
            return errors;
        }
    }

    preValidate(deck: Deck, card: Card, isFavorite: boolean): ValidationMessage[] | null {
        const hasCardInDeck = deck.normalCards.findIndex(item => item.card.isEqual(card)) >= 0 || deck.favoriteCards.findIndex(item => item.isEqual(card)) >= 0;
        const insertCount = (hasCardInDeck ? 1 : DeckConfig.CountOfIfNewStack);

        //If Already overflow, is ignore.
        if (deck.totalCardCount <= DeckConfig.DeckTotalCount
            && (deck.totalCardCount + insertCount) > DeckConfig.DeckTotalCount) {
            return [{
                key: "EqualTotalDeckNumberOfSheets-Greater",
                level: "warning",
                message: `カード枚数が${DeckConfig.DeckTotalCount}枚を超えます。`
            }];
        }
        return null;
    }

}

export class EqualTotalNumberOfFavoriteCards implements DeckRule {

    validate(deck: Deck): ValidationMessage[] | null {
        const errors: ValidationMessage[] = [];
        const totalCount = deck.favoriteCards.length;

        if (totalCount < 1) {
            errors.push({
                key: "EqualTotalNumberOfFavoriteCards-Less",
                level: "muted-warning",
                message: `推しカードが設定されていません。`
            });
        } else if (totalCount > DeckConfig.FavoriteCardCount) {
            errors.push({
                key: "EqualTotalNumberOfFavoriteCards-Greater",
                level: "warning",
                message: `推しカードが${DeckConfig.DeckTotalCount}枚を超えています。`
            });
        }

        if (errors.length === 0) {
            return null;
        } else {
            return errors;
        }
    }

    preValidate(deck: Deck, card: Card, isFavorite: boolean): ValidationMessage[] | null {
        if (isFavorite) {
            if (deck.favoriteCards.length === DeckConfig.FavoriteCardCount) {
                return [{
                    key: "EqualTotalNumberOfFavoriteCards-Greater",
                    level: "warning",
                    message: `推しカードの合計枚数が${DeckConfig.FavoriteCardCount}枚を超えます。`
                }];
            }
        }
        return null;
    }
}

export class LessNumberOfDuplicateCards implements DeckRule {

    validate(deck: Deck): ValidationMessage[] | null {
        const errors: ValidationMessage[] = this.getIllegalCards(deck).map(x => {
            return {
                key: "LessNumberOfDuplicateCards-Greater",
                level: "warning",
                message: `[${x.cardNo}]が${DeckConfig.DuplicateCardCount}枚以上登録されています。`
            };
        });
        
        if (errors.length === 0) {
            return null;
        } else {
            return errors;
        }
    }

    getIllegalCards(deck: Deck) {
        const cards = deck.aggregateCards();
        return cards.getAllCards()
            .filter(x => x.count > DeckConfig.DuplicateCardCount)
            .map(x => x.card);
    }

    preValidate(deck: Deck, card: Card, isFavorite: boolean): ValidationMessage[] | null {
        if (!isFavorite) {
            //合計枚数（推しも含める）を計算する
            const stackCard = deck.normalCards.find(value => value.card.isEqual(card));
            const favoriteCards = deck.favoriteCards.filter(favCard => favCard.isEqual(card)).length;
            const stackCardCount = stackCard ? stackCard.count : 0;

            if ((favoriteCards + stackCardCount) === DeckConfig.DuplicateCardCount) {
                return [{
                    key: "LessNumberOfDuplicateCards-Greater",
                    level: "warning",
                    message: `[${card.cardNo} ${card.cardName}]の登録枚数が${DeckConfig.DuplicateCardCount}枚を超えます。`
                }];
            }
        }
        return null;
    }
}

export class LessNumberOfProducts implements DeckRule {

    validate(deck: Deck): ValidationMessage[] | null {
        const errors: ValidationMessage[] = [];
        const productIds = deck.extractProductIds();
        const totalCount = productIds.length;

        if (totalCount > DeckConfig.IncludableProductCount) {
            errors.push({
                key: "LessNumberOfProducts-Greater",
                level: "warning",
                message: `異なる作品は${DeckConfig.IncludableProductCount}つまで登録できます。`
            });
        }

        if (errors.length === 0) {
            return null;
        } else {
            return errors;
        }
    }

    preValidate(deck: Deck, card: Card, isFavorite: boolean): ValidationMessage[] | null {
        const productIds = deck.extractProductIds();
        const totalCount = productIds.length;

        if (totalCount >= DeckConfig.IncludableProductCount) {
            //現在、商品数が満杯のとき、リストにない商品を追加するときに警告する
            if (productIds.find(id => id === card.cardProductId) === undefined) {
                const names = productIds.map(id => ProductRepository.getProduct(id).title).join('・');
                return [{
                    key: "LessNumberOfProducts-Greater",
                    level: "warning",
                    message: `すでに異なる作品「${names}」が登録されています。`
                }];
            }
        }

        return null;
    }
}